<template>
  <ion-page>
    <ion-header>
      <ion-toolbar>
        <ion-title>{{ $t('app.tabs.rewards') }}</ion-title>
        <ion-buttons slot="end">
          <ion-button @click="toggleFilter" data-cy="filter-button">
            <ion-icon :icon="filterOutline" />
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content>
      <ion-refresher slot="fixed" @ionRefresh="refreshData($event)">
        <ion-refresher-content></ion-refresher-content>
      </ion-refresher>

      <template v-if="isLoading">
        <div class="px-md py-lg text-center md:max-w-[600px] mx-auto md:my-lg" data-cy="skeleton-points-summary">
          <div class="w-[160px] h-[14px] mx-auto">
            <ion-skeleton-text class="w-full h-full" :animated="true"></ion-skeleton-text>
          </div>
          <div class="w-[180px] h-[24px] mx-auto mt-2">
            <ion-skeleton-text class="w-full h-full" :animated="true"></ion-skeleton-text>
          </div>
        </div>

        <div class="w-[95%] h-[44px] mx-auto my-4 overflow-hidden md:max-w-[600px]" data-cy="skeleton-segment">
          <ion-skeleton-text class="w-full h-full" :animated="true"></ion-skeleton-text>
        </div>

        <div class="mx-auto md:max-w-[600px]">
          <ion-list class="px-sm md:max-w-[600px] md:mx-auto" data-cy="skeleton-rewards-list">
            <ion-item-divider sticky class="py-2">
              <div class="w-[120px] h-[18px]">
                <ion-skeleton-text class="w-full h-full" :animated="true"></ion-skeleton-text>
              </div>
            </ion-item-divider>

            <ion-item v-for="n in 3" :key="n" class="my-xs mx-0 py-sm bg-white" 
              style="--padding-start: 16px; --padding-end: 16px; --inner-padding-end: 0;"
              data-cy="skeleton-reward-item">
              <div slot="start" class="w-[48px] h-[48px] flex items-center justify-center">
                <ion-skeleton-text class="w-full h-full" :animated="true"></ion-skeleton-text>
              </div>
              
              <ion-label>
                <div class="w-[70%] h-[18px] mb-md">
                  <ion-skeleton-text class="w-full h-full" :animated="true"></ion-skeleton-text>
                </div>
                <div class="w-[90%] h-[16px] mb-sm">
                  <ion-skeleton-text class="w-full h-full" :animated="true"></ion-skeleton-text>
                </div>
                <div class="w-[30%] h-[14px] mb-sm">
                  <ion-skeleton-text class="w-full h-full" :animated="true"></ion-skeleton-text>
                </div>
              </ion-label>

              <div slot="end" class="w-[68px] h-[24px] mr-1">
                <ion-skeleton-text class="w-full h-full rounded-sm" :animated="true"></ion-skeleton-text>
              </div>
              </ion-item>
          </ion-list>
        </div>
      </template>

      <template v-else>
        <div class="text-center py-lg px-md bg-background-secondary mb-md md:max-w-[600px] mx-auto md:my-lg" data-cy="points-summary">
          <ion-text color="medium" class="text-sm">{{ $t('common.totalPoints') }}</ion-text>
          <div class="text-2xl font-bold text-primary mt-xs points-display" data-cy="points-display">{{ userPoints }}</div>
        </div>

        <ion-segment v-model="currentFilter" class="mx-md my-md md:max-w-[600px] md:mx-auto md:my-lg" style="--background: var(--color-background-secondary);" data-cy="filter-segment">
          <ion-segment-button value="all" data-cy="filter-all">{{ $t('rewards.allRewards') }}</ion-segment-button>
          <ion-segment-button value="available" data-cy="filter-available">{{ $t('rewards.availableRewards') }}</ion-segment-button>
          <ion-segment-button value="redeemed" data-cy="filter-redeemed">{{ $t('rewards.redeemedRewards') }}</ion-segment-button>
        </ion-segment>

        <div class="mx-auto md:max-w-[600px]">
          <ion-list class="md:max-w-[600px]" data-cy="rewards-list">
            <!-- Display quests section for advanced users -->
            <ion-item-group v-if="isAdvancedSegment && filteredQuests.length > 0" data-cy="quests-group">
              <ion-item-divider sticky class="py-md px-md bg-stone-100" data-cy="quests-divider">
                <ion-label>{{ $t('rewards.quests') }}</ion-label>
              </ion-item-divider>

              <ion-item
                v-for="quest in filteredQuests"
                :key="quest.id"
                class="my-xs mx-0 bg-white border-l-[3px] border-l-success"
                style="--padding-start: var(--space-md); --padding-end: var(--space-md); --padding-top: var(--space-sm); --padding-bottom: var(--space-sm);"
                button
                @click="handleQuestClick(quest)"
                data-cy="quest-item"
              >
                <ion-thumbnail slot="start" class="flex items-center justify-center mr-md rounded-base quest-thumbnail">
                  <ion-icon :icon="iconMap[quest.icon as keyof typeof iconMap] || trophyOutline" size="large" class="text-2xl text-success"></ion-icon>
                </ion-thumbnail>
                
                <ion-label>
                  <h2 class="text-base font-semibold text-text-primary mb-xs" data-cy="quest-title">{{ quest.name }}</h2>
                  <p class="text-sm text-text-secondary mb-xs" data-cy="quest-description">{{ quest.description }}</p>
                  
                  <div class="mt-xs" data-cy="quest-progress">
                    <ion-progress-bar 
                      :value="getQuestProgress(quest)" 
                      color="success"
                      data-cy="quest-progress-bar"
                    ></ion-progress-bar>
                    <div class="text-xs text-text-tertiary mt-xs text-right" data-cy="quest-progress-text">
                      {{ $t('rewards.questProgress', {
                        progress: quest.tags?.progress || 0,
                        target: quest.tags?.target || 0
                      }) }}
                    </div>
                  </div>
                </ion-label>

                <ion-badge
                  slot="end"
                  color="success"
                  class="px-sm py-xs text-xs font-semibold"
                  data-cy="quest-points-badge"
                >
                  +{{ quest.points_awarded }} {{ $t('leaderboard.pts') }}
                </ion-badge>
              </ion-item>
            </ion-item-group>

            <!-- Display reward categories -->
            <ion-item-group 
              v-for="(categoryRewards, category) in groupedRewards" 
              :key="category"
              data-cy="rewards-group"
            >
              <ion-item-divider sticky class="py-md px-md text-black text-md bg-stone-100" data-cy="category-divider">
                <ion-label>{{ getCategoryTranslation(category) }}</ion-label>
              </ion-item-divider>

              <ion-item
                v-for="reward in categoryRewards"
                :key="reward.id"
                class="my-xs mx-0 bg-white"
                style="--padding-start: var(--space-md); --padding-end: var(--space-md); --padding-top: var(--space-sm); --padding-bottom: var(--space-sm);"
                button
                @click="handleRewardClick(reward)"
                :class="{ 'opacity-70': !isAffordable(reward) }"
                data-cy="reward-item"
              >
                <ion-thumbnail slot="start" class="flex items-center justify-center mr-md bg-background-secondary rounded-base reward-thumbnail">
                  <ion-icon :icon="iconMap[reward.icon as keyof typeof iconMap] || giftOutline" size="large" class="text-2xl text-primary"></ion-icon>
                </ion-thumbnail>
                
                <ion-label>
                  <h2 class="text-base font-semibold text-text-primary mb-xs" data-cy="reward-title">{{ reward.name }}</h2>
                  <p class="text-sm text-text-secondary mb-xs" data-cy="reward-description">{{ reward.description }}</p>
                  <p class="text-xs" :class="{ 'text-text-tertiary': isAffordable(reward), 'text-danger': !isAffordable(reward) }" data-cy="points-required">
                    {{ reward.points_required }} {{ $t('common.points') }}
                  </p>
                </ion-label>

                <ion-badge 
                  slot="end" 
                  :color="isAffordable(reward) ? 'primary' : 'medium'" 
                  class="px-sm py-xs text-xs font-semibold"
                  data-cy="points-badge"
                >
                  {{ reward.points_required }} {{ $t('leaderboard.pts') }}
                </ion-badge>
              </ion-item>
            </ion-item-group>

            <!-- Empty state when no rewards match filter -->
            <div v-if="Object.keys(groupedRewards).length === 0" class="text-center py-xl px-md text-text-secondary" data-cy="empty-state">
              <ion-icon :icon="sadOutline" size="large" class="text-[48px] text-text-tertiary mb-md"></ion-icon>
              <h3 class="text-lg mb-sm">{{ $t('rewards.noRewardsAvailable') }}</h3>
            </div>
          </ion-list>
        </div>
      </template>

      <!-- Reward detail modal -->
      <reward-detail-modal
                :reward="selectedReward"
        :user-points="userPoints"
                :is-affordable="isAffordable"
                :on-redeem="redeemReward"
        :icon-map="iconMap"
                @close="selectedReward = null"
              />

      <!-- Quest detail modal -->
      <quest-detail-modal
        :quest="selectedQuest"
        :get-quest-progress="getQuestProgress"
        :icon-map="iconMap"
        @close="selectedQuest = null"
      />
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, computed } from 'vue';
import { useRewardsData } from '../composables/useRewardsData';
import { useI18n } from 'vue-i18n';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonRefresher,
  IonRefresherContent,
  IonList,
  IonItem,
  IonItemDivider,
  IonItemGroup,
  IonLabel,
  IonThumbnail,
  IonBadge,
  IonSegment,
  IonSegmentButton,
  IonSkeletonText,
  IonButton,
  IonButtons,
  IonIcon,
  IonText,
  IonProgressBar,
  useBackButton
} from '@ionic/vue';
import {
  filterOutline,
  giftOutline,
  walletOutline,
  cafeOutline,
  fastFoodOutline,
  ribbonOutline,
  diamondOutline,
  ticketOutline,
  starOutline,
  cardOutline,
  discOutline,
  sadOutline,
  trophyOutline,
  calendarOutline,
  shareOutline,
  checkmarkDoneOutline
} from 'ionicons/icons';
import RewardDetailModal from '../components/RewardDetailModal.vue';
import QuestDetailModal from '../components/QuestDetailModal.vue';

const i18n = useI18n();

// Map of icon strings to actual icon components
const iconMap = {
  giftOutline,
  walletOutline,
  cafeOutline,
  fastFoodOutline,
  ribbonOutline,
  diamondOutline,
  ticketOutline,
  starOutline,
  cardOutline,
  discOutline,
  trophyOutline,
  calendarOutline,
  shareOutline,
  checkmarkDoneOutline
};

const {
  quests,
  isLoading,
  isAdvancedSegment,
  currentFilter,
  selectedReward,
  selectedQuest,
  userPoints,
  groupedRewards,
  loadData,
  refreshData: composableRefreshData,
  isAffordable,
  handleRewardClick,
  handleQuestClick,
  getQuestProgress,
  redeemReward
} = useRewardsData();

// Filter quests based on the same filter as rewards
const filteredQuests = computed(() => {
  if (!isAdvancedSegment.value) return [];
  
  if (currentFilter.value === 'redeemed') {
    // Quests cannot be redeemed in the same way as rewards, so hide them in redeemed tab
    return [];
  }
  
  // 'available' shows all quests as they don't have a points requirement
  return quests.value;
});

onMounted(async () => {
  await loadData();
  
  // Handle hardware back button for modals using Ionic's useBackButton
  useBackButton(10, () => {
    if (selectedReward.value) {
      selectedReward.value = null;
      return;
    }
    if (selectedQuest.value) {
      selectedQuest.value = null;
      return;
    }
  });
});

onBeforeUnmount(() => {
  // Cleanup is handled automatically by useBackButton
});

function toggleFilter() {
  // TODO: Implement filter options popup
}

async function refreshData(event: CustomEvent) {
  const completionCallback = await composableRefreshData(event);
  // Handle completion in the component layer
  if (completionCallback) {
    completionCallback();
  }
}

function getCategoryTranslation(category: string): string {
  const categoryMap: Record<string, string> = {
    'Store Credit': i18n.t('rewards.storeCredit'),
    'Food & Beverage': i18n.t('rewards.foodBeverage'),
    'Membership': i18n.t('rewards.membership'),
    'Quests': i18n.t('rewards.quests'),
    'Other': i18n.t('rewards.other')
  };
  
  return categoryMap[category] || category;
}
</script>

<style scoped>
/* Custom CSS for components that can't be easily styled with Tailwind */
.quest-thumbnail {
  --size: 60px;
  background: rgba(var(--ion-color-success-rgb), 0.1);
}

.reward-thumbnail {
  --size: 60px;
}
</style> 